import { useCallback, useMemo, useState } from 'react'
import { useRisingEdgeEffect } from 'src/clientLib/hooks'
import {
  createScenario,
  getScenarios
} from 'src/redux/slices/project/endpoints'
import { CreateScenarioArg } from 'src/redux/slices/project/types'
import { TextFieldOnChangeEvent } from 'src/types/event-handlers'

import {
  Button,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  TextField
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/AddCircle'
import CancelIcon from '@material-ui/icons/Cancel'

///// Types
type ScenarioCreatorProps = {
  disabled: boolean
}

///// Component
const ScenarioCreator: React.FC<ScenarioCreatorProps> = ({ disabled }) => {
  const { data: scenarios, isLoading: isLoadingScenarios } = getScenarios.useQuery('')
  const [showCreator, setShowCreator] = useState(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [upgradeSource, setUpgradeSource] = useState<number | ''>('')

  const scenarioData: CreateScenarioArg = useMemo(
    () => ({
      title,
      description,
      sort_order: scenarios?.length ?? -1,
      measure_definitions:
        upgradeSource !== '' ? scenarios?.[upgradeSource]?.measure_definitions : {},
    }),
    [title, description, scenarios, upgradeSource],
  )

  const [trigger, { data, isLoading }] = createScenario.useMutation({
    fixedCacheKey: 'createScenario',
  })

  const submit = useCallback(() => {
    trigger(scenarioData)
  }, [trigger, scenarioData])

  const toggleShowCreator = useCallback(() => {
    setShowCreator((prev) => !prev)
  }, [setShowCreator])

  const handleChangeTitle = useCallback(
    (e: TextFieldOnChangeEvent): void => setTitle(e.target.value),
    [setTitle],
  )
  const handleChangeDescription = useCallback(
    (e: TextFieldOnChangeEvent): void => setDescription(e.target.value),
    [setDescription],
  )

  const handleChangeScenarioToCopy = useCallback(
    (e: TextFieldOnChangeEvent): void => {
      setUpgradeSource(Number(e.target.value))
    },
    [setUpgradeSource],
  )

  useRisingEdgeEffect({
    callback: toggleShowCreator,
    condition: data && !isLoading,
  })

  return (
    <Grid container item justifyContent='flex-start' alignItems='center'>
      {!showCreator && (
        <Grid item xs={12}>
          <Button
            endIcon={<AddIcon />}
            variant='contained'
            size='small'
            color='primary'
            onClick={toggleShowCreator}
            disabled={disabled}
            style={{ width: '100%' }}
          >
            New Scenario
          </Button>
        </Grid>
      )}
      {showCreator && (
        <Fade in={showCreator} timeout={500}>
          <Grid container item alignItems='center' spacing={2} style={{ marginTop: '1rem' }}>
            <Grid item>
              <TextField
                helperText='Enter scenario name'
                label='Scenario Name'
                value={title}
                onChange={handleChangeTitle}
                size='small'
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                helperText='Enter scenario description'
                label='Scenario Description'
                value={description}
                onChange={handleChangeDescription}
                size='small'
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                helperText='Select a scenario'
                label='Copy upgrades from another scenario'
                size='small'
                value={upgradeSource}
                onChange={handleChangeScenarioToCopy}
              >
                {scenarios?.map(({ id, title }, i) => (
                  <MenuItem value={i} key={`scenario-${title}-${id}`}>
                    {' '}
                    {title}{' '}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item>
              <Button
                disabled={isLoadingScenarios || title.length === 0}
                variant='outlined'
                onClick={submit}
              >
                Create
              </Button>
            </Grid>
            <Grid item>
              <IconButton onClick={toggleShowCreator}>
                <CancelIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Fade>
      )}
    </Grid>
  )
}

export default ScenarioCreator
